import {
  Tooltip,
  type TooltipContentProps,
  type TooltipPortalProps,
  type TooltipRootOptions,
} from "@kobalte/core/tooltip";
import { createPresence } from "@solid-primitives/presence";
import {
  type Accessor,
  createContext,
  createSignal,
  type JSX,
  type ParentComponent,
  Show,
  type Signal,
  splitProps,
  useContext,
} from "solid-js";
import { twMerge } from "tailwind-merge";
import { AnimateChildren } from "../animation/AnimateChildren";

const defaultSuppressed: Accessor<boolean> = () => false;

const StTooltipSuppressionContext = createContext<Accessor<boolean>>(defaultSuppressed);

/**
 * When the accessor returns true, {@link StTooltip} instances use Kobalte `disabled` so hints do not open.
 */
export const StTooltipSuppressionProvider: ParentComponent<{ suppressed: Accessor<boolean> }> = (props) => (
  <StTooltipSuppressionContext.Provider value={props.suppressed}>{props.children}</StTooltipSuppressionContext.Provider>
);

export function useStTooltipSuppressed(): Accessor<boolean> {
  return useContext(StTooltipSuppressionContext);
}

/**
 * Maps a string to `title` when tooltip suppression is off (same rule as {@link StTooltip}).
 * Use for native HTML `title` tooltips so they hide with "Hide tooltips" / "Hide all".
 */
export function useNativeTooltipTitle(): (label: string | undefined | null | false) => string | undefined {
  const suppressed = useStTooltipSuppressed();
  return (label) => (suppressed() || label === undefined || label === null || label === false ? undefined : label);
}

export function StTooltip(
  props: {
    noArrow?: boolean;
    dontKeepOpenOnHover?: boolean;
    children: JSX.Element;
    content: JSX.Element;
    portalProps?: Omit<TooltipPortalProps, "children">;
    contentProps?: TooltipContentProps & JSX.HTMLAttributes<HTMLDivElement>;
  } & TooltipRootOptions
) {
  const signal = props.onOpenChange ? ([() => props.open, props.onOpenChange] as Signal<boolean>) : createSignal(false);
  const [open, setOpen] = signal;

  const duration = 100;
  const openDelay = props.openDelay ?? 50;
  const closeDelay = props.closeDelay ?? 50;
  const presence = createPresence(open, {
    enterDuration: duration + openDelay,
    exitDuration: duration + closeDelay,
  });

  const [split, rest] = splitProps(props, ["content", "portalProps", "contentProps", "noArrow", "dontKeepOpenOnHover"]);
  const suppressed = useStTooltipSuppressed();

  return (
    <Tooltip
      openDelay={openDelay}
      closeDelay={closeDelay}
      onOpenChange={setOpen}
      open={open()}
      {...rest}
      disabled={Boolean(rest.disabled) || suppressed()}
      forceMount={presence.isMounted()}
    >
      {rest.children}

      <Tooltip.Portal {...split.portalProps}>
        <AnimateChildren
          show={open()}
          duration={duration}
          presence={presence}
          enter={{
            scale: [0.9, 1],
            opacity: [0, 1],
            delay: openDelay,
          }}
          exit={{
            scale: 0.9,
            opacity: 0,
            delay: closeDelay,
          }}
        >
          {(anim) => (
            <Tooltip.Content
              {...split.contentProps}
              ref={anim.ref}
              class={twMerge(
                `z-100 origin-(--kb-tooltip-content-transform-origin) rounded-lg border border-primary-border bg-tile px-2 py-1 text-sm text-primary-text shadow-xl`,
                split.dontKeepOpenOnHover && "pointer-events-none!",
                split.contentProps?.class
              )}
            >
              <Show when={!split.noArrow}>
                <Tooltip.Arrow />
              </Show>

              {split.content}
            </Tooltip.Content>
          )}
        </AnimateChildren>
      </Tooltip.Portal>
    </Tooltip>
  );
}

export const StTooltipTrigger = Tooltip.Trigger;
